import { create } from 'zustand'
import type { FleetEfficiency } from '../lib/range'
import type {
  Actionable,
  Breakdown,
  BusStatusEvent,
  Alert, ApiState, Cell, GeofenceEvent, GeoPayload, HubVisit, InboundRow, Kpi, LiveCounts,
  MbmtStatusEvent,
  RouteProgress, StreamEvent, TimsTrip, Trip, Triage, Vehicle, VehicleMeta, VehicleTrack,
} from '../lib/types'
import { indexFences, type FenceIndex } from '../lib/pointInPolygon'
import { lastSeen } from '../lib/format'
import type { HeatMode } from '../map/layers/heatmap'
import type { BasemapMode } from '../map/basemaps'

/**
 * One store for the whole console.
 *
 * Live records arrive as a firehose (40 vehicles × ~5 s, plus a cell window per minute),
 * so mutation here is deliberately cheap: Maps keyed by id, replaced wholesale only when
 * React needs to re-render. The map layers do NOT subscribe to per-vehicle changes --
 * they read the store imperatively on an animation frame, because pushing 40 position
 * updates a second through React reconciliation is how you drop to 12 fps.
 */

export type ConnState = 'connecting' | 'live' | 'reconnecting'

/** Cells older than this stop being drawn: a congestion reading is a claim about now. */
export const CELL_TTL_MS = 15 * 60 * 1000

export interface Banner {
  id: string
  tone: 'warn' | 'error' | 'info'
  message: string
}

interface FleetState {
  vehicles: Map<string, Vehicle>
  cells: Map<string, Cell>
  kpis: Map<string, Kpi>
  alerts: Alert[]
  quality: Record<string, number>
  stats: ApiState['stats'] | null

  meta: Map<string, VehicleMeta>
  /** TIMS logistics overlay by fleet vehicle_id. Kafka meta is not overwritten. */
  timsTrips: Map<string, TimsTrip>
  /** Triage state by alert_id. Absent means 'new'. */
  triage: Map<string, Triage>
  /** Live MBMT vehicle telemetry (position/speed/heading/soc/moving) by vehicle_id, from
   *  the SAME shared /events connection as everything else in this store -- CLAUDE.md's
   *  live-data section. useMbmtStatusEvents.ts is a thin selector over this field, not a
   *  second EventSource; see that file's own history for the connection it replaced. */
  mbmtStatus: Record<string, MbmtStatusEvent>
  /** Live TUPL (Ulhasnagar) vehicle status/position by vehicle_id -- same reasoning and
   *  shape as mbmtStatus above; independent map, own 'tupl_status' event, same
   *  MbmtStatusEvent shape reused as-is (position/speed/heading/soc/moving/updated_ts is
   *  generic, not MBMT-specific). useTuplStatusEvents.ts is the thin selector over this. */
  tuplStatus: Record<string, MbmtStatusEvent>
  /** Live Nagpur bus status/position by vehicle_id -- same reasoning as mbmtStatus above,
   *  replacing useBusStatusEvents.ts's own former EventSource. */
  busStatus: Record<string, BusStatusEvent>
  /** Vehicles a supervisor has marked down. Keyed by vehicle_id; see types.Breakdown. */
  breakdowns: Map<string, Breakdown>
  /** Latest admin correction to a (possibly long-resolved) breakdown record -- a resolved
   *  incident has no home in `breakdowns` above (vehicle_id-keyed, active only), so
   *  BreakdownsPanel's own "All breakdowns" table watches this instead of a collection,
   *  to patch its row in place on any other open console, not just the editor's own tab. */
  breakdownEdit: (StreamEvent & { type: 'breakdown_edited' })['data'] | null
  /** Live count snapshot from SSE (TV wall KPIs without refresh). */
  liveCounts: LiveCounts | null
  /** Open breakdowns + unresolved criticals from SSE. */
  actionables: Actionable[]
  /** The alert a supervisor has opened, if any. */
  openAlert: string | null
  /** A vehicle's actual driven track, pulled on demand and drawn over the corridor. */
  track: VehicleTrack | null
  /** One-shot camera request. The map consumes it and clears it. */
  flyRequest: { lon: number; lat: number; zoom?: number; nonce: number } | null

  trips: Trip[]
  visits: HubVisit[]
  fenceEvents: GeofenceEvent[]
  progress: Map<string, RouteProgress>
  /** The fleet's MEASURED kWh/km by load, from v_efficiency_by_load. Refreshed
   *  periodically; used in place of the hardcoded prior wherever a truck has no
   *  efficiency of its own yet. */
  fleetEfficiency: FleetEfficiency
  setFleetEfficiency: (f: FleetEfficiency) => void

  /** /api/reports/arrival_board rows, keyed by vehicle_id. Polled every 20s by
   *  CommandCentre.tsx -- the same fresh-from-Postgres km_to_go ArrivalBoard.tsx uses to
   *  override live progress's own (sometimes stale/wrong-leg/missing) km_remaining. Shared
   *  here so the map popup (an imperative, non-React MapCanvas.tsx) can apply the same
   *  override via overrideProgressWithArrival, instead of quoting a different distance for
   *  the same truck than the Arrivals board does. */
  arrivalBoard: Map<string, InboundRow>
  setArrivalBoard: (rows: InboundRow[]) => void

  geofences: GeoPayload['geofences'] | null
  routes: GeoPayload['routes'] | null
  fenceIndex: FenceIndex[]
  hasTiles: boolean
  tilesVersion: string
  /**
   * Which basemap is under the fleet. Starts 'vector' (the self-hosted offline-first
   * archive, see map/basemaps.ts's own header) and is promoted to 'google2d' by
   * MapCanvas.tsx as soon as a Maps Platform session can be created -- billed but visually
   * richer, matching what the operator explicitly asked for; the runtime 'error' handler
   * in that same file falls back to 'vector' again if Google's tiles stop loading, so this
   * field can drift from its initial value without any user action either direction.
   * OSM/satellite/etc. remain manual opt-in. Photoreal 3D is a separate overlay mode,
   * also opt-in, also billed.
   */
  basemapMode: BasemapMode
  mapsKey: string
  photorealAvailable: boolean
  /** Whether map/basemaps.ts's Google 2D tile session was created successfully -- gates
   *  BasemapSwitch.tsx's 'google2d' pill the same way photorealAvailable gates 'photoreal'. */
  google2dAvailable: boolean
  attribution: string[]

  conn: ConnState
  banners: Banner[]
  selectedVehicle: string | null
  selectedFence: string | null
  heatMode: HeatMode
  /** Bumped on every batch of live records; map layers watch this instead of the Maps. */
  revision: number

  applyBootstrap: (s: ApiState) => void
  applyGeo: (g: GeoPayload) => void
  applyEvent: (e: StreamEvent) => void
  applyStats: (s: ApiState['stats'], quality: Record<string, number>) => void
  /** Re-syncs the full cell set from /api/state -- see useFleetData's own heal-poll
   *  comment for why cells specifically need this: the SSE channel drops messages on
   *  every one of its routine ~13-16s reconnects with no replay, and a cell only emits
   *  once a minute, so a dropped update can leave a stale score on screen indefinitely
   *  with nothing to self-correct it (unlike roster/tims_trips/live_counts, which this
   *  same poll already re-syncs). */
  refreshCells: (cells: Cell[]) => void
  setConn: (c: ConnState) => void
  addBanner: (b: Banner) => void
  dismissBanner: (id: string) => void
  selectVehicle: (id: string | null) => void
  selectFence: (id: string | null) => void
  setHeatMode: (m: HeatMode) => void
  setBasemapMode: (m: BasemapMode) => void
  applyConfig: (c: { photoreal: boolean; maps_key: string }) => void
  setGoogle2dAvailable: (available: boolean) => void
  setAttribution: (a: string[]) => void
  openAlertDetail: (id: string | null) => void
  /** Open an alert AND put the map on the truck it is about. See the implementation. */
  openAlertAndReveal: (id: string) => void
  setTriage: (t: Triage) => void
  setTrack: (t: VehicleTrack | null) => void
  flyTo: (p: { lon: number; lat: number; zoom?: number }) => void
}

const ALERT_CAP = 500
const LOG_CAP = 300

/** Merges a fresh /api/state roster onto the existing one, per vehicle_id, instead of
 *  replacing it wholesale.
 *
 *  serving runs 2 replicas behind a LoadBalancer with no session affinity, each holding
 *  its own independent in-memory roster (they don't share state). The roster-sync poll in
 *  useFleetData.ts hits /api/state every 8 seconds with no guarantee of landing on the
 *  same replica twice in a row -- a wholesale replace meant that whichever replica's
 *  Kafka consumer happened to be a few seconds further behind on a given tick could
 *  regress a vehicle's last_obd_ts/last_geo_ts, which vehicleState() (lib/format.ts)
 *  reads against STALE_AFTER_MS to decide online vs. offline. The whole fleet flipping
 *  offline and back within seconds, reported live in production, was this: not a real
 *  telemetry gap, a REGRESSION in freshness from re-polling a laggier replica.
 *
 *  A vehicle present in the new poll always keeps its OWN freshest sighting -- never
 *  older than what the client already knew, only ever moving forward. Vehicles absent
 *  from the new poll are still dropped (this is also how a server-side prune heals a
 *  ghost plate off the TV wall, per this file's own roster-sync comment) -- the fix is
 *  narrowly about not regressing a vehicle that's still present, not about ignoring
 *  removals. */
export function mergeRoster(existing: Map<string, Vehicle>, incoming: Vehicle[]): Map<string, Vehicle> {
  const next = new Map<string, Vehicle>()
  for (const v of incoming) {
    const prev = existing.get(v.vehicle_id)
    next.set(v.vehicle_id, prev && lastSeen(prev) > lastSeen(v) ? prev : v)
  }
  return next
}

/** Stamps `_seen` on cells hydrated from /api/state, same units and same meaning as the SSE
 *  path (applyEvent below) -- Cell's own `_seen` is a client bookkeeping field the API
 *  payload never carries, so a cell left unstamped reads as maximally stale to
 *  CommandCentre's own corridor-pressure filter (_seen > now - 15m) regardless of how
 *  fresh it actually is. Uses window_end (ms, the cell's own last-computed-window
 *  boundary), not Date.now() -- a cell whose real last update was 20 minutes ago should
 *  still read as stale, not artificially fresh just because the page happened to load. */
export function hydrateCells(cells: Cell[]): Map<string, Cell> {
  return new Map(cells.map((c) => [c.cell, { ...c, _seen: c.window_end / 1000 }]))
}

export const useFleet = create<FleetState>((set, get) => ({
  vehicles: new Map(),
  cells: new Map(),
  kpis: new Map(),
  alerts: [],
  quality: {},
  stats: null,

  meta: new Map(),
  timsTrips: new Map(),
  triage: new Map(),
  mbmtStatus: {},
  tuplStatus: {},
  busStatus: {},
  breakdowns: new Map(),
  breakdownEdit: null,
  liveCounts: null,
  actionables: [],
  openAlert: null,
  track: null,
  flyRequest: null,

  trips: [],
  visits: [],
  fenceEvents: [],
  progress: new Map(),
  fleetEfficiency: {},
  setFleetEfficiency: (fleetEfficiency) => set({ fleetEfficiency }),

  arrivalBoard: new Map(),
  setArrivalBoard: (rows) => set({ arrivalBoard: new Map(rows.map((r) => [r.vehicle_id, r])) }),

  geofences: null,
  routes: null,
  fenceIndex: [],
  hasTiles: false,
  tilesVersion: '',
  // The self-hosted vector archive: the only basemap that survives a depot with no route
  // out. Briefly changed to OSM as the default (daad3a5); reverted -- a remote raster
  // default meant a disconnected depot got a blank map with no error on first load, which
  // is the one failure mode this archive exists to avoid.
  basemapMode: 'vector',
  mapsKey: '',
  photorealAvailable: false,
  google2dAvailable: false,
  attribution: [],

  conn: 'connecting',
  banners: [],
  selectedVehicle: null,
  selectedFence: null,
  // Congestion is on by default: it is the layer that answers "is the corridor moving",
  // which is the question the board exists to answer.
  heatMode: 'congestion',
  revision: 0,

  applyBootstrap: (s) =>
    set((st) => ({
      vehicles: new Map(s.vehicles.map((v) => [v.vehicle_id, v])),
      // hydrateCells stamps _seen -- see its own comment. Before this fix, an unstamped
      // cell read as maximally stale, so corridor pressure showed empty/BALANCED on every
      // fresh load until enough SSE congestion events happened to trickle in and restamp
      // each cell one at a time -- reported live as "needs a refresh every time", which a
      // refresh never actually fixed, it just re-triggered the same unstamped bootstrap.
      cells: hydrateCells(s.cells),
      kpis: new Map(Object.entries(s.kpis ?? {})),
      alerts: s.alerts ?? [],
      quality: s.quality ?? {},
      stats: s.stats,
      meta: new Map(Object.entries(s.meta ?? {})),
      timsTrips: new Map(Object.entries(s.tims_trips ?? {})),
      triage: new Map(Object.entries(s.triage ?? {})),
      breakdowns: new Map(Object.entries(s.breakdowns ?? {})),
      liveCounts: s.live_counts ?? null,
      actionables: s.actionables ?? [],
      trips: s.trips ?? [],
      visits: s.visits ?? [],
      fenceEvents: s.fence_events ?? [],
      progress: new Map(Object.entries(s.progress ?? {})),
      revision: st.revision + 1,
    })),

  applyGeo: (g) =>
    set((st) => ({
      geofences: g.geofences,
      routes: g.routes,
      fenceIndex: indexFences(g.geofences?.features ?? []),
      hasTiles: g.tiles,
      tilesVersion: g.tiles_version ?? '',
      revision: st.revision + 1,
    })),

  applyEvent: (e) =>
    set((st) => {
      switch (e.type) {
        case 'vehicle': {
          const vehicles = new Map(st.vehicles)
          vehicles.set(e.data.vehicle_id, e.data)
          return { vehicles, revision: st.revision + 1 }
        }
        case 'cell': {
          const cells = new Map(st.cells)
          cells.set(e.data.cell, { ...e.data, _seen: Date.now() / 1000 })
          return { cells, revision: st.revision + 1 }
        }
        case 'kpi': {
          const kpis = new Map(st.kpis)
          kpis.set(e.data.vehicle_id, e.data)
          return { kpis, revision: st.revision + 1 }
        }
        case 'trip': {
          if (st.trips.some((t) => t.trip_id === e.data.trip_id)) return st
          return { trips: [e.data, ...st.trips].slice(0, LOG_CAP), revision: st.revision + 1 }
        }
        case 'visit': {
          return { visits: [e.data, ...st.visits].slice(0, LOG_CAP), revision: st.revision + 1 }
        }
        case 'fence': {
          return {
            fenceEvents: [e.data, ...st.fenceEvents].slice(0, LOG_CAP),
            revision: st.revision + 1,
          }
        }
        case 'triage': {
          const triage = new Map(st.triage)
          if (e.data.state === 'new') triage.delete(e.data.alert_id)
          else triage.set(e.data.alert_id, e.data)
          return { triage, revision: st.revision + 1 }
        }
        case 'breakdown': {
          const breakdowns = new Map(st.breakdowns)
          // The server sends the cleared vehicle back with no category, which is how a
          // repair is broadcast. Deleting on that keeps one code path for both directions.
          if ('category' in e.data && e.data.category) {
            breakdowns.set(e.data.vehicle_id, e.data as Breakdown)
          } else {
            breakdowns.delete(e.data.vehicle_id)
          }
          return { breakdowns, revision: st.revision + 1 }
        }
        case 'live_counts':
          return { liveCounts: e.data, revision: st.revision + 1 }
        case 'actionables':
          return { actionables: e.data.items ?? [], revision: st.revision + 1 }
        case 'progress': {
          const progress = new Map(st.progress)
          progress.set(e.data.vehicle_id, e.data)
          return { progress, revision: st.revision + 1 }
        }
        case 'vehicles_removed': {
          if (!e.data.vehicle_ids?.length) return st
          const vehicles = new Map(st.vehicles)
          let changed = false
          for (const id of e.data.vehicle_ids) {
            if (vehicles.delete(id)) changed = true
          }
          return changed ? { vehicles, revision: st.revision + 1 } : st
        }
        case 'roster':
          return {
            vehicles: mergeRoster(st.vehicles, e.data.vehicles ?? []),
            revision: st.revision + 1,
          }
        case 'tims_trips':
          return {
            timsTrips: new Map(Object.entries(e.data ?? {})),
            revision: st.revision + 1,
          }
        // Authoritative replace, not a merge -- same reasoning as 'roster' and
        // 'tims_trips' above. /api/state's own `triage` field is Postgres-backed, not the
        // in-memory dict a single SSE 'triage' event reflects (see serving/app.py's
        // _triage_snapshot_from_db), specifically so this poll heals a colleague's ack
        // that this tab's own SSE connection never saw -- the same "/events drops
        // messages on reconnect, with no replay" gap the roster/cells poll already exists
        // to paper over, just for triage state instead of vehicle positions.
        case 'triage_sync':
          return {
            triage: new Map(Object.entries(e.data ?? {})),
            revision: st.revision + 1,
          }
        // Per-vehicle merge, same shape useMbmtStatusEvents.ts's own former EventSource
        // handler used -- just dispatched from the one shared connection now instead of
        // a second one. e.data.updated_ts is checked by consumers (BusOpsShell.tsx)
        // against a staleness window, not here -- this just records the latest frame.
        case 'mbmt_status': {
          const mbmtStatus = { ...st.mbmtStatus, [e.data.vehicle_id]: e.data }
          return { mbmtStatus, revision: st.revision + 1 }
        }
        case 'tupl_status': {
          const tuplStatus = { ...st.tuplStatus, [e.data.vehicle_id]: e.data }
          return { tuplStatus, revision: st.revision + 1 }
        }
        case 'bus_status': {
          const busStatus = { ...st.busStatus, [e.data.vehicle_id]: e.data }
          return { busStatus, revision: st.revision + 1 }
        }
        case 'alert': {
          // Newest first, and deduped: the notifier is at-least-once by design, so the
          // same alert_id can legitimately arrive twice.
          if (st.alerts.some((a) => a.alert_id === e.data.alert_id)) return st
          return { alerts: [e.data, ...st.alerts].slice(0, ALERT_CAP), revision: st.revision + 1 }
        }
        // A reclassification run (tools/reclassify_overspeed_alerts.py, or a rule-change
        // cleanup like HUB_OVERSTAY's) flagged this alert superseded after it already
        // reached the tape. Without this, a false positive a browser already has stays on
        // screen -- a vehicle card's exception chip reads straight off this array -- until
        // that tab happens to reload.
        case 'alert_removed': {
          if (!st.alerts.some((a) => a.alert_id === e.data.alert_id)) return st
          return {
            alerts: st.alerts.filter((a) => a.alert_id !== e.data.alert_id),
            revision: st.revision + 1,
          }
        }
        // TIMS gate scans arrive late relative to a live alert; the driver this alert
        // was tagged with at the moment it fired can be wrong until the register
        // catches up. See _reconcile_alert_drivers in serving/app.py -- this is that
        // correction reaching an already-open tab, same shape as alert_removed above.
        case 'alert_updated': {
          if (!st.alerts.some((a) => a.alert_id === e.data.alert_id)) return st
          return {
            alerts: st.alerts.map((a) => (a.alert_id === e.data.alert_id ? e.data : a)),
            revision: st.revision + 1,
          }
        }
        case 'breakdown_edited':
          // A fresh object every time, including a repeat edit of the same breakdown_id --
          // BreakdownsPanel's useEffect keys off this reference, not a diff of its fields.
          return { breakdownEdit: e.data, revision: st.revision + 1 }
        default:
          return st
      }
    }),

  applyStats: (stats, quality) => set({ stats, quality }),
  refreshCells: (cells) => set((st) => ({ cells: hydrateCells(cells), revision: st.revision + 1 })),
  setConn: (conn) => set({ conn }),
  addBanner: (b) =>
    set((st) => (st.banners.some((x) => x.id === b.id) ? st : { banners: [...st.banners, b] })),
  dismissBanner: (id) => set((st) => ({ banners: st.banners.filter((b) => b.id !== id) })),
  selectVehicle: (selectedVehicle) => set((st) => ({
    selectedVehicle,
    selectedFence: null,
    revision: st.revision + 1,
  })),
  selectFence: (selectedFence) => set((st) => ({
    selectedFence,
    selectedVehicle: null,
    revision: st.revision + 1,
  })),
  setHeatMode: (heatMode) => set({ heatMode }),
  setBasemapMode: (basemapMode) => set({ basemapMode, attribution: [] }),
  applyConfig: (c) => set({ photorealAvailable: !!c.photoreal, mapsKey: c.maps_key || '' }),
  setGoogle2dAvailable: (available) => set({ google2dAvailable: available }),
  setAttribution: (attribution) => set({ attribution }),
  openAlertDetail: (openAlert) => set({ openAlert }),

  /**
   * Open an alert and go to the truck it is about.
   *
   * An operator clicking an alert is asking "where is it". Opening a panel and leaving the
   * map wherever it happened to be makes them do the second half by hand, on a corridor
   * 200 km long where the answer is rarely already on screen.
   *
   * THE LIVE POSITION WINS OVER THE ALERT'S. alert.lat/lon is where the truck was when the
   * rule FIRED, which for an idling or unscheduled-stop alert is minutes to hours ago --
   * flying there shows an empty stretch of road with no vehicle on it. The alert's own
   * position is the fallback, and it is the right fallback: if the truck has since gone
   * dark, where it was when it last said something IS the best information there is.
   *
   * It lives in the store rather than in a component because there are two places an alert
   * can be clicked -- the tape and the command-centre list -- and behaviour duplicated in
   * two components is behaviour that will differ in one of them.
   */
  openAlertAndReveal: (id) => {
    const st = get()
    st.openAlertDetail(id)
    const a = st.alerts.find((x: Alert) => x.alert_id === id)
    if (!a) return
    if (a.vehicle_id) st.selectVehicle(a.vehicle_id)
    const v = a.vehicle_id ? st.vehicles.get(a.vehicle_id) : undefined
    if (v?.lat != null && v?.lon != null) st.flyTo({ lon: v.lon, lat: v.lat, zoom: 14 })
    else if (a.lat != null && a.lon != null) st.flyTo({ lon: a.lon, lat: a.lat, zoom: 14 })
  },
  setTrack: (track) => set((st) => ({ track, revision: st.revision + 1 })),
  flyTo: (p) => set({ flyRequest: { ...p, nonce: Date.now() } }),
  setTriage: (t) => set((st) => {
    const triage = new Map(st.triage)
    if (t.state === 'new') triage.delete(t.alert_id)
    else triage.set(t.alert_id, t)
    return { triage, revision: st.revision + 1 }
  }),
}))

/** Cells still fresh enough to draw. During trip replay, keep the full cached snapshot. */
export function freshCells(cells: Map<string, Cell>, now = Date.now(), includeStale = false): Cell[] {
  if (includeStale) return [...cells.values()]
  const cutoff = (now - CELL_TTL_MS) / 1000
  const out: Cell[] = []
  for (const c of cells.values()) if ((c._seen ?? 0) > cutoff) out.push(c)
  return out
}

// Debug handle, deliberate. Same rationale as the map handle in MapCanvas: a console that
// renders nothing gives you no stack trace, and being able to ask the live store what it
// holds turns an hour of guessing into a single question.
;(globalThis as unknown as { __fleet?: typeof useFleet }).__fleet = useFleet
